import { FileReferencesSection } from "@/components/features/matter/file-repo/FileReferencesSection";
import { TrustCheckCard } from "@/components/features/matter/trust-checks/TrustCheckCard";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  CheckboxFormField,
  Form,
  FormSubmitButton,
  Separator,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { useUser } from "@/components/UserProvider";
import {
  getLienId,
  useLiensViewModel,
} from "@/hooks/matter-view-models/use-liens-view-model";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useSyncLienContactConnections } from "@/lib/data/react-query/connections/lien-contact-connection";
import {
  useCreateMatterLien,
  useDeleteMatterLien,
  useUpdateMatterLien,
} from "@/lib/data/react-query/matters/matter-lien";
import { ContactConnectionInFormSchema } from "@/lib/data/schemas/contact/ContactSchema";
import { MatterLienSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { MATTER_CONTACT_CONNECTION_DEFAULT_VALUES } from "@/lib/data/static/formStatics";
import {
  MATTER_LIEN_CATEGORY_OPTIONS,
  MATTER_LIEN_SUBCATEGORY_OPTIONS_BY_CATEGORY,
} from "@/lib/data/static/matters/lienStatics";
import { MATTER_LIEN_STATUS_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type { Contact, MatterLien } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { mergeFilesAndReferenceId } from "@/lib/utils/matterFile";
import { getConnectionFormContact } from "@/lib/utils/connections/item-contact-connections";
import {
  createLienContactConnectionChildInput,
  getLienContactConnection,
  type SupportedLienContactRole,
} from "@/lib/utils/connections/lien-contact-connections";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { isNil, omit, omitBy } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

interface AddOrEditLienModalProps {
  title: string;
  description?: string;
  editing?: boolean;
  matterId: string;
  lienId?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  initialData?: Partial<MatterLien>;
}

const getContactId = (
  contact?: (Partial<Contact> & { contactId?: string }) | null,
) => contact?.id ?? contact?.contactId ?? "";

const lienFormSchema = MatterLienSchema.pick({
  label: true,
  originalAmount: true,
  negotiatedAmount: true,
  paidAmount: true,
  description: true,
  status: true,
  type: true,
  category: true,
  subCategory: true,
  goalAmount: true,
  reductionFeeAgreement: true,
})
  .extend({
    goalAmount: z.preprocess(
      (value) => (value === "" ? null : value),
      MatterLienSchema.shape.goalAmount,
    ),
    negotiatedAmount: z.preprocess(
      (value) => (value === "" ? null : value),
      MatterLienSchema.shape.negotiatedAmount,
    ),
    originalAmount: z.preprocess(
      (value) => (value === "" ? null : value),
      MatterLienSchema.shape.originalAmount,
    ),
    paidAmount: z.preprocess(
      (value) => (value === "" ? null : value),
      MatterLienSchema.shape.paidAmount,
    ),
    matterContactConnectionLienHolder: ContactConnectionInFormSchema,
    matterContactConnectionLienRepresentative: ContactConnectionInFormSchema,
  })
  .superRefine((data, ctx) => {
    if (!getContactId(data.matterContactConnectionLienHolder)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Lien holder is required",
        path: ["matterContactConnectionLienHolder"],
      });
    }
  });

const defaultFormValues = {
  label: "",
  originalAmount: null,
  negotiatedAmount: null,
  paidAmount: null,
  description: undefined,
  status: undefined,
  type: undefined,
  category: undefined,
  subCategory: null,
  goalAmount: null,
  reductionFeeAgreement: false,
  matterContactConnectionLienHolder: MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionLienRepresentative:
    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
};

const stripLienContactFormFields = (data: z.infer<typeof lienFormSchema>) =>
  omit(data, [
    "matterContactConnectionLienHolder",
    "matterContactConnectionLienRepresentative",
  ]);

const getLienFormValues = (
  lien: Record<string, unknown> | undefined,
): z.infer<typeof lienFormSchema> => ({
  ...defaultFormValues,
  label: (lien?.label as string | undefined) ?? "",
  originalAmount: (lien?.originalAmount as number | null | undefined) ?? null,
  negotiatedAmount:
    (lien?.negotiatedAmount as number | null | undefined) ?? null,
  paidAmount: (lien?.paidAmount as number | null | undefined) ?? null,
  description: (lien?.description as string | null | undefined) ?? undefined,
  status: (lien?.status as string | null | undefined) ?? undefined,
  type: (lien?.type as string | null | undefined) ?? undefined,
  category:
    (lien?.category as
      | z.infer<typeof lienFormSchema>["category"]
      | null
      | undefined) ?? undefined,
  subCategory:
    (lien?.subCategory as z.infer<typeof lienFormSchema>["subCategory"]) ??
    null,
  goalAmount: (lien?.goalAmount as number | null | undefined) ?? null,
  reductionFeeAgreement:
    (lien?.reductionFeeAgreement as boolean | undefined) ?? false,
});

export const AddOrEditLienModal = ({
  title,
  description,
  editing,
  matterId,
  lienId,
  open,
  setOpen,
  initialData,
}: AddOrEditLienModalProps) => {
  const { hasActionPermission } = useUser();
  const { updateSlideoverPayload } = useModals();
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const liensViewModel = useLiensViewModel(matterId);
  const matter = matterData.data;

  const lien = liensViewModel.find((lien) => getLienId(lien) === lienId);
  const lienInitialValues = mergeInitialData(lien, initialData);
  const activeLienId = lienInitialValues ? getLienId(lienInitialValues) : "";

  const [isDeleting, setIsDeleting] = useState(false);
  const [, setSelectedLienHolder] = useState<Contact | null>(null);
  const [, setSelectedLienRepresentative] = useState<Contact | null>(null);

  const lienHolder = getLienContactConnection(lien, "Lien Holder");
  const lienRepresentative = getLienContactConnection(
    lien,
    "Lien Representative",
  );

  const lienForm = useForm<z.infer<typeof lienFormSchema>>({
    resolver: zodResolver(lienFormSchema),
    values: editing
      ? {
          ...getLienFormValues(lienInitialValues as Record<string, unknown>),
          matterContactConnectionLienHolder:
            getConnectionFormContact(lienHolder),
          matterContactConnectionLienRepresentative:
            getConnectionFormContact(lienRepresentative),
        }
      : defaultFormValues,

    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const syncLienConnections = useSyncLienContactConnections();

  const createLien = useCreateMatterLien({ showToast: false });
  const updateLien = useUpdateMatterLien({ showToast: false });
  const deleteLien = useDeleteMatterLien();

  const isFormLoading =
    createLien.isPending ||
    updateLien.isPending ||
    syncLienConnections.isPending;

  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const lienModalFormId = "matter-lien-modal-form";

  const onSubmit = async (formValues: z.infer<typeof lienFormSchema>) => {
    const normalizedData = {
      ...stripLienContactFormFields(formValues),
      subCategory: formValues.subCategory ?? null,
    };
    const contactIdsByRole = {
      "Lien Holder": getContactId(formValues.matterContactConnectionLienHolder),
      "Lien Representative": getContactId(
        formValues.matterContactConnectionLienRepresentative,
      ),
    } satisfies Record<SupportedLienContactRole, string>;
    let resData;
    if (editing && lien) {
      const result = await updateLien.mutateAsync({
        ...lien,
        ...normalizedData,
      });
      resData = result;
      const resultLienId = getLienId(result.data);
      await syncLienConnections.mutateAsync({
        currentLien: lien,
        desiredContactIdsByRole: contactIdsByRole,
        lienId: resultLienId,
        matterId: matter.id,
      });
    } else {
      const connections = Object.entries(contactIdsByRole).flatMap(
        ([role, contactId]) =>
          contactId
            ? [
                createLienContactConnectionChildInput(
                  contactId,
                  role as SupportedLienContactRole,
                ),
              ]
            : [],
      );
      const result = await createLien.mutateAsync({
        matterId: matter.id,
        ...normalizedData,
        connections,
      });
      resData = result;
    }
    toast.success(
      editing ? "Lien updated successfully" : "Lien created successfully",
    );
    if (closeAfterSave) {
      setOpen(false);
      lienForm.reset({
        ...defaultFormValues,
        ...omitBy(resData.data, isNil),
      });
      setSelectedLienHolder(null);
      setSelectedLienRepresentative(null);
    } else {
      updateSlideoverPayload("lien", {
        title: SLIDEOVER_TITLE.LIEN.UPDATE,
        description: SLIDEOVER_DESCRIPTION.LIEN.UPDATE,
        editing: true,
        lienId: getLienId(resData.data),
      });
    }
  };

  useEffect(() => {
    if (!open) {
      lienForm.reset();
      setSelectedLienHolder(null);
      setSelectedLienRepresentative(null);
    }
  }, [open, lienForm]);

  const selectedLienCategory = lienForm.watch("category");
  const lienSubCategoryItems = selectedLienCategory
    ? MATTER_LIEN_SUBCATEGORY_OPTIONS_BY_CATEGORY[selectedLienCategory]
    : [];

  const lienPaidStatusFormValue = lienForm.watch("status");
  const lienPaidAmountFormValue = lienForm.watch("paidAmount");
  const lienNegotiatedAmountFormValue = lienForm.watch("negotiatedAmount");
  const lienOriginalAmountFormValue = lienForm.watch("originalAmount");
  const fileReferences = mergeFilesAndReferenceId(
    matter,
    "lienId",
    activeLienId,
  );

  useEffect(() => {
    if (lienPaidStatusFormValue === "Paid") {
      lienForm.setValue(
        "paidAmount",
        lienNegotiatedAmountFormValue || lienOriginalAmountFormValue,
      );
    } else if (lienPaidStatusFormValue === "Unpaid") {
      lienForm.setValue("paidAmount", 0);
    }
  }, [
    lienPaidStatusFormValue,
    lienPaidAmountFormValue,
    lienNegotiatedAmountFormValue,
    lienOriginalAmountFormValue,
    lienForm,
  ]);

  const lienModalContactPickerProps = {
    contactPickerPresentation: "modal" as const,
    contactSelectorSize: "compact" as const,
    hideCompactContactChrome: true,
  };

  const renderLienHolderContactPreview = useCallback(
    (contact: Contact) => (
      <MatterContactConnectionModalPreview
        contactId={contact.id}
        previewContact={contact}
        matterId={matter.id}
        matterContactConnection={
          lienHolder && lienHolder.targetObjectId === getContactId(contact)
            ? lienHolder
            : ({
                label: "Lien holder",
                contactId: contact.id,
              } as never)
        }
        onRemoveContact={() => {
          lienForm.setValue(
            "matterContactConnectionLienHolder",
            MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
            { shouldDirty: true },
          );
        }}
      />
    ),
    [lienHolder, matter.id, lienForm],
  );

  const renderLienRepresentativeContactPreview = useCallback(
    (contact: Contact) => (
      <MatterContactConnectionModalPreview
        contactId={contact.id}
        previewContact={contact}
        matterId={matter.id}
        matterContactConnection={
          lienRepresentative &&
          lienRepresentative.targetObjectId === getContactId(contact)
            ? lienRepresentative
            : ({
                label: "Lien representative",
                contactId: contact.id,
              } as never)
        }
        onRemoveContact={() => {
          lienForm.setValue(
            "matterContactConnectionLienRepresentative",
            MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
            { shouldDirty: true },
          );
        }}
      />
    ),
    [lienRepresentative, matter.id, lienForm],
  );

  const lienModalContactSidebar = (
    <div className="flex flex-col gap-2">
      <AddContactFormField
        {...lienModalContactPickerProps}
        label="Lien holder*"
        register={lienForm.register("matterContactConnectionLienHolder")}
        setSelectedContact={setSelectedLienHolder}
        defaultSearchObject={{}}
        onDelete={() => {
          lienForm.setValue(
            "matterContactConnectionLienHolder",
            MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
            { shouldDirty: true },
          );
        }}
        matterId={matter.id}
        defaultSearchField="name"
        renderSelectedContactPreview={renderLienHolderContactPreview}
      />
      <AddContactFormField
        {...lienModalContactPickerProps}
        label="Lien representative"
        register={lienForm.register(
          "matterContactConnectionLienRepresentative",
        )}
        setSelectedContact={setSelectedLienRepresentative}
        defaultSearchObject={{
          individualSubTypes: ["Lien Representative"],
        }}
        onDelete={() => {
          lienForm.setValue(
            "matterContactConnectionLienRepresentative",
            MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
            { shouldDirty: true },
          );
        }}
        matterId={matter.id}
        defaultValues={{
          type: "individual",
          individualSubTypes: ["Lien Representative"],
        }}
        renderSelectedContactPreview={renderLienRepresentativeContactPreview}
      />
    </div>
  );

  const lienFormFields = (
    <div className="overflow-y-auto">
      <fieldset
        className={cn(
          slideoverFieldsetClassName,
          "my-0 p-4 sm:p-5",
          "grid-cols-1 md:grid-cols-2",
        )}
        disabled={
          editing
            ? !hasActionPermission(
                ACTION_PERMISSION_KEYS.matterLien,
                ACTION_PERMISSION_VALUES.update,
              )
            : false
        }
      >
        <TextFormField register={lienForm.register("label")} label="Label*" />
        <SimpleSelectFormField
          register={lienForm.register("category")}
          label="Category"
          items={MATTER_LIEN_CATEGORY_OPTIONS}
          placeholder="Select category"
          clearable
          onChange={() => {
            lienForm.setValue("subCategory", null, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          formatItemLabel={(item) => item}
        />
        <SimpleSelectFormField
          key={selectedLienCategory ?? "none"}
          register={lienForm.register("subCategory")}
          label="Subcategory"
          items={lienSubCategoryItems}
          placeholder="Select subcategory"
          disabled={!selectedLienCategory}
          clearable
          formatItemLabel={(item) => item}
        />
        <CurrencyFormField
          register={lienForm.register("originalAmount")}
          label="Original amount*"
        />
        <CurrencyFormField
          register={lienForm.register("negotiatedAmount")}
          label="Negotiated amount"
        />
        <SimpleSelectFormField
          register={lienForm.register("status")}
          label="Status"
          items={MATTER_LIEN_STATUS_OPTIONS}
          placeholder="Select status"
          clearable
        />
        <CurrencyFormField
          register={lienForm.register("paidAmount")}
          label="Paid amount"
          disabled={["Paid", "Unpaid"].includes(lienPaidStatusFormValue || "")}
        />
        <CurrencyFormField
          register={lienForm.register("goalAmount")}
          label="Goal amount"
        />
        <CheckboxFormField
          register={lienForm.register("reductionFeeAgreement")}
          label="Reduction fee agreement"
        />
        <div className="col-span-2">
          <TextAreaFormField
            register={lienForm.register("description")}
            label="Description"
          />
        </div>
        <div className="col-span-full mt-6 grid grid-cols-subgrid gap-y-2">
          <div className="col-span-full">
            <Separator />
          </div>
          <FileReferencesSection
            editing={editing}
            fileReferences={fileReferences}
            matter={matter}
            itemKey="lienId"
            itemId={activeLienId}
            permissionKey={ACTION_PERMISSION_KEYS.matterLien}
          />
        </div>
      </fieldset>
      {lien && lien.trustChecks.length > 0 && (
        <div className="bg-light-primary/50 m-6 flex flex-col gap-y-2 rounded-md p-2">
          <Typography variant="heading">Trust checks</Typography>
          <div className="flex flex-col gap-y-2">
            {lien.trustChecks.map((trustCheck) => (
              <TrustCheckCard
                key={trustCheck.id}
                trustCheck={trustCheck}
                matter={matter}
                className="border-border border"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Form {...lienForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          size="4xl"
          loading={isFormLoading}
          isDirty={lienForm.formState.isDirty}
          preview={lienModalContactSidebar}
          previewLabel={null}
          bodyFormId={lienModalFormId}
          bodyFormOnSubmit={lienForm.handleSubmit(onSubmit)}
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                {editing &&
                  lien &&
                  hasActionPermission(
                    ACTION_PERMISSION_KEYS.matterLien,
                    ACTION_PERMISSION_VALUES.delete,
                  ) && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => {
                        setIsDeleting(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={lienForm.formState.isDirty}
                />
                <FormSubmitButton
                  submitFormId={lienModalFormId}
                  title={editing ? "Save" : "Add lien"}
                  loading={isFormLoading}
                  overrideDisabled={Boolean(initialData)}
                  submitFn={() => {
                    setCloseAfterSave(true);
                  }}
                  disabled={isFormLoading}
                />
              </div>
            </div>
          }
        >
          {lienFormFields}
        </AppModal>
      </Form>

      {editing && lien && (
        <ActionConfirmationModal
          open={isDeleting}
          setOpen={setIsDeleting}
          title="Are you sure you want to delete this lien?"
          description="This action cannot be undone. This will remove the lien and all associated tasks and files."
          afterActionFn={() => {
            deleteLien.mutate({
              matterId: matter.id,
              id: getLienId(lien),
            });
            setOpen(false);
          }}
        />
      )}
    </>
  );
};
